import { useNavigate } from 'react-router-dom';
import { useTenant } from '../context/TenantContext.js';
import { subscribers, payments as paymentsApi, plans as plansApi } from '../api/client.js';
import { useApi } from '../hooks/useApi.js';
import { KpiCard } from '../components/KpiCard.js';
import { LoadingSpinner } from '../components/LoadingSpinner.js';
import { ErrorMessage } from '../components/ErrorMessage.js';
import type { SubscriberStats, PaymentStats, PaymentSession, Plan } from '../types/index.js';

export function OverviewPage() {
  const navigate = useNavigate();
  const { activeTenantId, activeTenant, loading: tenantLoading, error: tenantError, refreshTenants } = useTenant();

  const { data: subStats, loading: subLoading, error: subError, refetch: refetchSubs } = useApi<SubscriberStats>(
    activeTenantId ? () => subscribers.stats(activeTenantId) : null,
    [activeTenantId],
  );

  const { data: payStats, loading: payLoading, error: payError, refetch: refetchPay } = useApi<PaymentStats>(
    activeTenantId ? () => paymentsApi.stats(activeTenantId) : null,
    [activeTenantId],
  );

  const { data: recentPayments, loading: recentLoading } = useApi<{ payments: PaymentSession[] }>(
    activeTenantId ? () => paymentsApi.list(activeTenantId, { limit: 8 }) : null,
    [activeTenantId],
  );

  const { data: planData } = useApi<{ plans: Plan[] }>(
    activeTenantId ? () => plansApi.list(activeTenantId) : null,
    [activeTenantId],
  );

  const activePlans = planData?.plans?.filter((p) => p.active) ?? [];
  const showGettingStarted = activePlans.length === 0 || !activeTenant?.botActive;

  if (tenantLoading || subLoading || payLoading) {
    return <LoadingSpinner text="Loading overview..." />;
  }

  if (tenantError) return <ErrorMessage message={tenantError} onRetry={() => void refreshTenants()} />;
  if (!activeTenantId) return <ErrorMessage message="No server selected. You may not be assigned to any server yet." />;
  if (subError) return <ErrorMessage message={subError} onRetry={() => void refetchSubs()} />;
  if (payError) return <ErrorMessage message={payError} onRetry={() => void refetchPay()} />;

  const revenue = payStats?.totalRevenueUsd ?? '0.00';
  const hasRevenue = Number(revenue) > 0;
  const dailyRevenue = payStats?.dailyRevenue ?? [];
  const activeSubs = subStats?.activeSubscribers ?? 0;
  const churnRate = subStats?.churnRate30d ?? '0.0';
  const mrr = subStats?.mrr ?? '0';

  return (
    <>
      {showGettingStarted && (
        <div className="getting-started-card">
          <h3>Getting Started</h3>
          <div
            className={`checklist-item ${activePlans.length > 0 ? 'done' : ''}`}
            onClick={() => navigate('/plans')}
          >
            <div className="checklist-check">
              {activePlans.length > 0 && (
                <svg viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="3" style={{ width: 10, height: 10 }}>
                  <polyline points="20 6 9 17 4 12" />
                </svg>
              )}
            </div>
            Create your first plan
          </div>
          <div
            className={`checklist-item ${activeTenant?.embedConfig ? 'done' : ''}`}
            onClick={() => navigate('/embeds')}
          >
            <div className="checklist-check">
              {activeTenant?.embedConfig && (
                <svg viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="3" style={{ width: 10, height: 10 }}>
                  <polyline points="20 6 9 17 4 12" />
                </svg>
              )}
            </div>
            Customize your embed
          </div>
          <div
            className={`checklist-item ${activeTenant?.botActive ? 'done' : ''}`}
            onClick={() => navigate('/settings')}
          >
            <div className="checklist-check">
              {activeTenant?.botActive && (
                <svg viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="3" style={{ width: 10, height: 10 }}>
                  <polyline points="20 6 9 17 4 12" />
                </svg>
              )}
            </div>
            Configure your bot token
          </div>
          <div className="checklist-item">
            <div className="checklist-check" />
            Test the /menu command in Discord
          </div>
        </div>
      )}

      <div className="kpi-row">
        <KpiCard
          label="Total Revenue"
          value={`$${Number(revenue).toLocaleString('en-US', { minimumFractionDigits: 2 })}`}
          color="violet"
          icon={
            <svg viewBox="0 0 24 24" fill="none" stroke="var(--violet)" strokeWidth="2.5">
              <line x1="12" y1="1" x2="12" y2="23" />
              <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" />
            </svg>
          }
        />
        <KpiCard
          label="Active Subscribers"
          value={String(activeSubs)}
          color="teal"
          icon={
            <svg viewBox="0 0 24 24" fill="none" stroke="var(--teal)" strokeWidth="2.5">
              <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
              <circle cx="9" cy="7" r="4" />
            </svg>
          }
        />
        <KpiCard
          label="Churn Rate"
          value={`${churnRate}%`}
          color="amber"
          icon={
            <svg viewBox="0 0 24 24" fill="none" stroke="var(--amber)" strokeWidth="2.5">
              <polyline points="23 6 13.5 15.5 8.5 10.5 1 18" />
            </svg>
          }
        />
        <KpiCard
          label="Monthly Recurring"
          value={`$${Number(mrr).toLocaleString('en-US', { minimumFractionDigits: 2 })}`}
          color="green"
          icon={
            <svg viewBox="0 0 24 24" fill="none" stroke="var(--green)" strokeWidth="2.5">
              <polyline points="23 6 13.5 15.5 8.5 10.5 1 18" />
              <polyline points="17 6 23 6 23 12" />
            </svg>
          }
        />
      </div>

      {/* revenue chart */}
      <div className="chart-card">
        <div className="chart-header">
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <span className="chart-title">Revenue</span>
            {hasRevenue && <span className="live-pill">Live</span>}
          </div>
        </div>
        <svg viewBox="0 0 700 180" preserveAspectRatio="none" style={{ width: '100%', height: 180, display: 'block' }}>
          <defs>
            <linearGradient id="cfill" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="rgba(129,140,248,0.15)" />
              <stop offset="100%" stopColor="rgba(129,140,248,0)" />
            </linearGradient>
            <linearGradient id="cstroke" x1="0" y1="0" x2="1" y2="0">
              <stop offset="0%" stopColor="#818cf8" />
              <stop offset="100%" stopColor="#5eead4" />
            </linearGradient>
          </defs>
          {hasRevenue && dailyRevenue.length > 0 ? (
            <>
              <path d={buildChartFill(dailyRevenue)} fill="url(#cfill)" />
              <path d={buildChartLine(dailyRevenue)} fill="none" stroke="url(#cstroke)" strokeWidth="2.5" />
            </>
          ) : (
            <>
              <path d="M0,170 L700,170 L700,180 L0,180 Z" fill="url(#cfill)" />
              <line x1="0" y1="170" x2="700" y2="170" stroke="url(#cstroke)" strokeWidth="2" strokeDasharray="8 4" />
              <text x="350" y="90" textAnchor="middle" fill="var(--t3)" fontSize="13" fontFamily="var(--fd)">No revenue data yet</text>
            </>
          )}
        </svg>
      </div>

      {/* split: activity feed + stats */}
      <div className="split-grid">
        <div className="feed-card">
          <div className="feed-title">Recent Activity</div>
          {recentLoading && <LoadingSpinner text="Loading..." />}
          {recentPayments?.payments.length === 0 && (
            <div style={{ fontSize: 13, color: 'var(--t3)', padding: '20px 0' }}>
              No payment activity yet
            </div>
          )}
          {recentPayments?.payments.map((p) => (
            <div key={p.id} className="feed-item">
              <div className={`feed-dot ${getFeedDotColor(p.state)}`} />
              <div className="feed-text">
                <strong>{p.discordUserId.slice(0, 8)}...</strong>{' '}
                {getFeedText(p.state, p.planName)}
              </div>
              <div className="feed-time">{timeAgo(p.createdAt)}</div>
              <div className="feed-amount">
                {p.state === 'COMPLETED' ? '+' : ''}${Number(p.priceUsd).toFixed(2)}
              </div>
            </div>
          ))}
        </div>

      </div>
    </>
  );
}

// helpers

function getFeedDotColor(state: string): string {
  switch (state) {
    case 'COMPLETED': return 'green';
    case 'AWAITING_PAYMENT': return 'amber';
    case 'EXPIRED':
    case 'FAILED': return 'red';
    default: return 'violet';
  }
}

function getFeedText(state: string, planName: string | null): string {
  const plan = planName ?? 'a plan';
  switch (state) {
    case 'COMPLETED': return `paid for ${plan}`;
    case 'AWAITING_PAYMENT': return `payment pending for ${plan}`;
    case 'EXPIRED': return `session expired for ${plan}`;
    case 'FAILED': return `payment failed for ${plan}`;
    case 'UNDERPAID': return `underpaid for ${plan}`;
    case 'SWEEPING': return `sweeping payment for ${plan}`;
    default: return `${state.toLowerCase()} — ${plan}`;
  }
}

function timeAgo(dateStr: string): string {
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
}


function buildChartLine(data: { day: string; revenue: string }[]): string {
  if (data.length === 0) return '';
  const values = data.map((d) => Number(d.revenue));
  const maxVal = Math.max(...values, 0.01);
  const W = 700;
  const H = 170;
  const PAD = 10;

  const points = values.map((v, i) => ({
    x: (i / Math.max(data.length - 1, 1)) * W,
    y: PAD + (H - PAD) * (1 - v / maxVal),
  }));

  const p0 = points[0]!;
  let d = `M${p0.x},${p0.y}`;
  for (let i = 1; i < points.length; i++) {
    const prev = points[i - 1]!;
    const cur = points[i]!;
    const cpx = (prev.x + cur.x) / 2;
    d += ` C${cpx},${prev.y} ${cpx},${cur.y} ${cur.x},${cur.y}`;
  }
  return d;
}

function buildChartFill(data: { day: string; revenue: string }[]): string {
  const line = buildChartLine(data);
  if (!line) return '';
  const last = data.length - 1;
  const W = 700;
  const lastX = (last / Math.max(data.length - 1, 1)) * W;
  return `${line} L${lastX},180 L0,180 Z`;
}
